import { useState } from "react";
import { API_ENDPOINTS } from "../config/constants";

const useVehicleData = (
  page,
  sort,
  selectedTag,
  searchFromUrl,
  startsWithFromUrl,
  selectedMake,
  selectedModel,
  stateRestoredRef,
  statePreserved,
  highestLoadedPageRef,
  forceNewFetchRef,
  location,
  navigate,
  setNavigatingToVehicle
) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchVehicles = async (forceFetch = false) => {
    // Calculate if we're loading a new page (beyond what we've loaded before)
    const isLoadingNewPage = page > highestLoadedPageRef.current || forceFetch || forceNewFetchRef.current;
    
    console.log("fetchVehicles called with:", {
      page,
      highestLoadedPage: highestLoadedPageRef.current,
      forceFetch,
      forceNewFetchRef: forceNewFetchRef.current,
      calculatedIsLoadingNewPage: isLoadingNewPage
    });
    
    // Skip fetch ONLY IF:
    // 1. We've restored state AND
    // 2. We're not loading a new page AND
    // 3. We have vehicles loaded already
    if (!isLoadingNewPage && 
        ((statePreserved && vehicles.length > 0) || 
         (stateRestoredRef.current && vehicles.length > 0))) {
      console.log("State preserved or restored, skipping fetch", {
        statePreserved,
        stateRestoredRef: stateRestoredRef.current,
        vehiclesCount: vehicles.length,
        isLoadingNewPage,
        page,
        highestLoadedPage: highestLoadedPageRef.current,
        forceFetch
      });
      setLoading(false); // Ensure loading is set to false
      
      // Reset the force fetch flag
      forceNewFetchRef.current = false;
      
      return;
    }
    
    console.log("ACTUALLY FETCHING for page", page, {
      isLoadingNewPage,
      highestLoadedPage: highestLoadedPageRef.current,
      forceFetch
    });
    
    // If we're actually fetching, update the highest page we've loaded
    if (page > highestLoadedPageRef.current) {
      highestLoadedPageRef.current = page;
    }
    
    // Reset the force fetch flag
    forceNewFetchRef.current = false;
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "12",
        sort,
      });

      if (selectedTag) {
        params.append("tag", selectedTag);
      }
      if (searchFromUrl) {
        params.append("search", searchFromUrl);
        if (new URLSearchParams(location.search).get("singleDigitMode") === "true") {
          params.append("singleDigitMode", "true");
        }
      } else if (startsWithFromUrl) {
        params.append("startsWith", startsWithFromUrl);
      }

      if (selectedMake) {
        params.append("make", selectedMake);
      }

      if (selectedModel) {
        params.append("model", selectedModel);
      }

      const response = await fetch(
        `${API_ENDPOINTS.EXPLORE_VEHICLES}?${params.toString()}`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();

      if (response.ok) {
        setError(null);

        // If we have exactly one result and it's from a search, redirect to that plate
        if (
          searchFromUrl &&
          data.vehicles.length === 1 &&
          data.pagination.total === 1
        ) {
          const formattedSearch = searchFromUrl
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, "");
          const vehicle = data.vehicles[0];

          console.log(vehicle.plate, formattedSearch);
          if (vehicle.plate === formattedSearch) {
            // Track navigation to vehicle before redirect
            setNavigatingToVehicle();
            navigate(`/plate/${vehicle.plate}`);
            return;
          }
        }

        if (page === 1) {
          setVehicles(data.vehicles);
        } else {
          setVehicles((prev) => {
            const existingIds = new Set(prev.map((v) => v._id));
            const newVehicles = data.vehicles.filter(
              (v) => !existingIds.has(v._id)
            );
            return [...prev, ...newVehicles];
          });
        }
        setHasMore(data.vehicles.length === 12);
      } else {
        throw new Error(data.message || "Failed to fetch vehicles");
      }
    } catch (err) {
      if (page === 1 || vehicles.length === 0) {
        setError(err.message);
      } else {
        console.error("Error loading more vehicles:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    vehicles,
    setVehicles,
    loading,
    setLoading,
    error,
    setError,
    hasMore,
    setHasMore,
    fetchVehicles
  };
};

export default useVehicleData; 